import { Link } from "react-router-dom";
import { DateTime as DT } from "luxon";
import ObjectAction from "./ObjectRows/ObjectAction";
import ObjectPin from "./ObjectRows/ObjectPin";
import { formatPrice } from "utils/helpers/functions/formatPrice";
import {
    withBaseCurrency,
    withLocalCurrency,
    withPrimaryCurrency,
} from "utils/helpers/currency/currencyHelpers";
import useChangeDate from "hooks/useChangeDate";
import * as apiObject from "api/apiObjects";
import { useToast } from "context/ToastContext";
import { useTranslation } from "react-i18next";
import ObjectStatusBadge from "../ObjectStatus/ObjectStatusBadge";
import { useObject } from "../context/ObjectProvider";
import ObjectVat from "../ObjectVat/ObjectVat";
import FormInput from "components/forms/FormInput";
import { useState } from "react";
import { useSettings } from "context/SettingsContext";
import { isObjectPaymentsCovered, getObjectDebtAmount } from "../helpers/objectPayCoverage";
import { getObjectCardPath } from "utils/helpers/objects/objectCardPath";
import OffHistoryDeadline from "../../OffHistoryDeadline";
import { getLocale } from "utils/helpers/functions/getLocale";
import { UserAvatarStack, UserNameItem } from "modules/user";
import { useAuth } from "context/AuthContext";
import { isManagerUser } from "utils/helpers/access/hasUserAccess";
import EToolTip from "components/others/EToolTip";
import SolarIcon from "components/icons/SolarIcon";
import { ClientNameItem } from "modules/client";
import { TaskTermInterval } from "modules/task";
import { TaskTypeStatus } from "@econtractor/types";
import vconst from "utils/helpers/structs/vconst";

const objectTermStatus = (objectStatus) => {
    if (objectStatus === vconst.objectFinished || objectStatus === vconst.objectFinishedTeam) {
        return TaskTypeStatus.COMPLETED;
    }
    if (objectStatus === vconst.objectReject) return TaskTypeStatus.CANCELLED;
    return TaskTypeStatus.INWORK;
};
 
const ObjectsTableRows = ({vobject, fetchObjects, setClientInfoId, setTasksObjectId, onShowSpecification, onOpenAccess}) => {

    const { t, i18n } = useTranslation(['Objects', 'common']);
    const { auth } = useAuth();
    const { showToast } = useToast();
    const { setShowSpinner, showDebtor, showClient, showTeam, showCreated, showAccess, showInWork, onlySale, onlyService, isSaleLike } = useObject();
    const canChangeAccess = isManagerUser(auth?.user);
    const colSpan = 5
        + (showCreated ? 1 : 0)
        + (showAccess ? 1 : 0)
        + (showInWork ? 1 : 0)
        + (showClient ? 1 : 0)
        + (showDebtor ? 1 : 0)
        + (showTeam ? 1 : 0);
    const { getSettings, localSettings } = useSettings();
    const cardPath = getObjectCardPath(
        vobject.ID,
        onlySale || vobject.onlySale,
        onlyService || vobject.onlyService,
    );
    const locale = getLocale(i18n.language);
    const dt = DT.fromMillis(vobject?.objectCreated||0).setLocale(locale).toLocaleString(DT.DATE_MED);
    const rate = Number(localSettings?.RATE?.current ?? 1) || 1;
    const isUahMode = rate === 1;
    const price = formatPrice(vobject?.objectPrice);
    const priceRate = formatPrice(vobject?.objectPriceRate);
    const paidUsd = Number(vobject?.paidUsd || 0);
    const paidUah = Number(vobject?.paidUah || 0);
    const paymentsCovered = isObjectPaymentsCovered({
        objectPrice: vobject?.objectPrice,
        objectPriceRate: vobject?.objectPriceRate,
        paidUsd,
        paidUah,
        rate,
    });
    const debtAmount = getObjectDebtAmount({
        objectPrice: vobject?.objectPrice,
        objectPriceRate: vobject?.objectPriceRate,
        paidUsd,
        paidUah,
        rate,
    });
    const paymentsColorClass = paymentsCovered ? 'text-success' : 'text-warning';
    const { changeDate } = useChangeDate(); 

    const [ showFormName, setShowFormName ] = useState(false);
    const [ showDeadline, setShowDeadline ] = useState(false);

    const handleClickDate = async (newCreatedDate) => {
        setShowSpinner(true);
        const resDate = await apiObject.setCreated(vobject.ID, newCreatedDate);
        await fetchObjects(true);
        if (!resDate) showToast(t('common:messError'), 'danger');
    }

    const handleSetName = async (newObjectName) => {
        setShowSpinner(true);
        const resName = await apiObject.setName(vobject.ID, newObjectName);
        await getSettings();
        if (!resName) showToast(t('common:messError'), 'danger');
        await fetchObjects(true);
    }

    const displayTitle = vobject.objectName || vobject.clientName;
 
    return (<>
        <tr>
            <td className="name">
                <Link to={cardPath}>
                <h6 className="mb-1 cursor-pointer text-truncate"> 
                    <span className="me-1">{displayTitle} </span>
                        {vobject.objectPower>0 && (<span className="d-inline-block fs-12 text-success">({vobject.objectPower} {t('labelKvt')})</span>)}
                        <div className="d-inline-block d-md-none"><ObjectVat isVat={vobject.objectVat}/></div>
                        </h6>
                {!showClient ? (
                    <p className={`f-12 mb-0 my-1 text-truncate ${vobject.clientName ? 'text-muted' : 'text-warning'}`}>
                        {vobject.clientName || t('noLeadLabel')}
                    </p>
                ) : null}
                </Link>
            </td>

            <td className="status">
                <ObjectStatusBadge status={vobject.objectStatus} />
            </td>

            {showClient ? (
                <td className="client">
                    {vobject.clientId ? (
                        <ClientNameItem
                            client={{
                                ID: vobject.clientId,
                                clientName: vobject.clientName,
                                clientPhone: vobject.clientPhone,
                            }}
                            onClick={setClientInfoId}
                        />
                    ) : (
                        <span className="text-warning f-12">{t('noLeadLabel')}</span>
                    )}
                </td>
            ) : null}

            {!isSaleLike ? (
            <td className="created">
                {vobject?.objectDeadline ? (
                    <TaskTermInterval
                        taskId={vobject.ID}
                        dateStart={vobject.objectCreated}
                        dateEnd={vobject.objectDeadline}
                        taskStatus={objectTermStatus(vobject.objectStatus)}
                        onStartClick={(e) => {
                            if (e.ctrlKey) changeDate(vobject?.objectCreated, handleClickDate);
                        }}
                        onEndClick={() => setShowDeadline(true)}
                        endTitle={t('toolDeadline')}
                    />
                ) : (
                    <h6
                        className="mb-1 cursor-pointer text-break"
                        onClick={(e) => {
                            if (e.ctrlKey) changeDate(vobject?.objectCreated, handleClickDate);
                        }}
                    >
                        {dt}
                    </h6>
                )}
            </td>
            ) : null}

            {showTeam ? (
            <td className={`team text-truncate ${vobject.teamId ? '' : 'text-danger'}`}>
                {vobject.teamName}
            </td>
            ) : null}

            {isSaleLike ? (
                <td className="created">
                    <h6
                        className="mb-1 cursor-pointer text-break"
                        onClick={(e)=> {
                            if (e.ctrlKey)
                                changeDate(vobject?.objectCreated, handleClickDate)}
                    }>{dt}</h6>
                </td>
            ) : null}

            {showCreated ? (
                <td className="author">
                    {vobject.ownerId || vobject.userName || vobject.userEmail ? (
                        <UserNameItem
                            smallMode
                            user={{
                                ID: vobject.ownerId,
                                userName: vobject.userName,
                                userEmail: vobject.userEmail,
                                userPicture: vobject.userPicture || undefined,
                            }}
                        />
                    ) : (
                        <span className="text-muted">—</span>
                    )}
                </td>
            ) : null}

            {showAccess ? (
                <td className="access">
                    <div className="d-flex align-items-center gap-2">
                        <UserAvatarStack
                            items={(vobject.accessUsers || []).map((grant) => ({
                                id: grant.ID,
                                name: grant.userName,
                                picture: grant.userPicture,
                                hint: `${grant.userName} — ${grant.accessLevel === 'edit' ? t('common:Users.accessLevelEdit') : t('common:Users.accessLevelView')}`,
                            }))}
                        />
                        {canChangeAccess ? (
                            <EToolTip title={t('common:buttons.change')}>
                                <button
                                    type="button"
                                    className="btn btn-link btn-sm p-0 text-primary access-edit"
                                    onClick={() => onOpenAccess?.(vobject)}
                                >
                                    <SolarIcon name="pencil" />
                                </button>
                            </EToolTip>
                        ) : null}
                    </div>
                </td>
            ) : null}

            {showInWork ? (
                <td className="in-work">
                    {vobject.inWorkUserId || vobject.inWorkUserName || vobject.inWorkUserEmail ? (
                        <UserNameItem
                            smallMode
                            user={{
                                ID: vobject.inWorkUserId,
                                userName: vobject.inWorkUserName,
                                userEmail: vobject.inWorkUserEmail,
                                userPicture: vobject.inWorkUserPicture || undefined,
                            }}
                        />
                    ) : (
                        <span className="text-muted">—</span>
                    )}
                </td>
            ) : null}

            {showDebtor ? (
                    <td className={`payments text-end ${paymentsColorClass}`}>
                        {paidUsd !== 0 || paidUah !== 0 ? (
                            <>
                                {paidUsd !== 0 ? (
                                    <h6 className={`mb-1 text-break ${paymentsColorClass}`}>
                                        {withBaseCurrency(formatPrice(paidUsd))}
                                    </h6>
                                ) : null}
                                {paidUah !== 0 ? (
                                    <p className={`f-12 mb-0 my-1 ${paymentsColorClass}`}>
                                        {withLocalCurrency(formatPrice(paidUah))}
                                    </p>
                                ) : null}
                            </>
                        ) : (
                            <span className={`f-12 ${paymentsColorClass}`}>—</span>
                        )}
                        {debtAmount > 0 ? (
                            <p className="text-danger fw-medium f-12 mb-0 mt-1">
                                <i className="bi bi-exclamation-circle me-1" title={t('labelDebt')}></i>
                                <span className="text-danger">
                                    {withPrimaryCurrency(formatPrice(debtAmount), rate)}
                                </span>
                            </p>
                        ) : null}
                    </td>
            ) : null}
            <td className="price text-end"> 
                <h6 className="mb-1 cursor-pointer text-break d-flex justify-content-end">
                    <div><ObjectVat isVat={vobject.objectVat}/></div>
                    {withPrimaryCurrency(price, rate)}
                </h6>
                {!isUahMode && (
                    <p className="text-muted f-12 mb-0 my-1">{withLocalCurrency(priceRate)}</p>
                )}
            </td>
            <td className="action">
                <div className="d-inline-flex align-items-center gap-1">
                    <ObjectPin
                        vobject={vobject}
                        fetchObjects={fetchObjects}/>
                    <ObjectAction 
                        setShowFormName={setShowFormName}
                        setClientInfoId={setClientInfoId}
                        setTasksObjectId={setTasksObjectId}
                        vobject={vobject}
                        onShowSpecification={onShowSpecification}
                        fetchObjects={fetchObjects}/>
                </div>
            </td>
          
        </tr>
        {showFormName &&
        (<tr>
            <td colSpan={colSpan}>
                 <div className="mb-3">
                    <FormInput 
                        require={true}
                        title={onlyService ? t('labelServiceSaleName') : onlySale ? t('labelSaleName') : t('labelName')}
                        showForm={showFormName}
                        setShowForm={setShowFormName}
                        callBack={handleSetName}
                        defaultValue={vobject.objectName || ''}/>
                </div>
            </td>
        </tr>)}
        <OffHistoryDeadline
            show={showDeadline}
            setShow={setShowDeadline}
            objectId={vobject.ID}
            objectDeadline={vobject?.objectDeadline || 0}
            onSuccess={async () => {
                await fetchObjects(true);
            }}
        />
    </>)
}

export default ObjectsTableRows
